import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { handleAuth } from "../Login/Auth";
import { getItem } from "../../utils/storage";
import formatDateWithTimezone from "../TimezoneDateFormatter/TimezoneDateFormatter";

const getToken = () => {
  return getItem("customerToken") || getItem("adminToken") || getItem("workerToken") || getItem("donorToken") || null;
};

/**
 * UserTimezoneDateFormatter Component
 * Automatically formats dates based on the user's timezone from their profile
 * 
 * This component automatically retrieves the user's timezone from their token
 * and formats dates accordingly. It handles all timezone conversions automatically.
 * 
 * Usage:
 * <UserTimezoneDateFormatter date={dateString} />
 * 
 * Props:
 * @param {string|Date} date - The date to format (required)
 * @param {string} dateFormat - Optional custom date format (defaults to user's preference)
 * @param {string} timeFormat - Optional custom time format "12h" or "24h" (defaults to user's preference)
 * @param {boolean} includeSeconds - Whether to include seconds (default: true)
 * @param {string} className - Optional CSS class name
 * @param {object} style - Optional inline styles
 */
const UserTimezoneDateFormatter = ({
  date,
  dateFormat: customDateFormat,
  timeFormat: customTimeFormat,
  includeSeconds = true,
  className = "",
  style = {},
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tokenDecode, setTokenDecode] = useState({});
  const [dateDecode, setDateDecode] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Public pages (LinkPayment, InvoicePayment) have no auth token – do NOT call handleAuth (would redirect to login)
        if (!getToken()) {
          setTokenDecode({});
          setDateDecode({});
          setIsLoading(false);
          return;
        }
        const res = await handleAuth(navigate, location);
        if (res?.data) {
          setTokenDecode(res.data || {});
          setDateDecode(res?.themes || {});
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- use pathname only; full `location` retriggers handleAuth every render (token_data storm).
  }, [navigate, location.pathname]);

  // Get user's timezone from tokenDecode
  const userTimezone = tokenDecode?.Timezone || "America/New_York";

  // Get date format from theme or use custom format
  const dateFormat = customDateFormat || dateDecode?.Format || "MM/DD/YYYY";

  // Get time format from user preferences or use custom format
  const timeFormat = customTimeFormat || tokenDecode?.TimeFormat || "12h";

  // Format the date
  const formattedDate = formatDateWithTimezone(
    date,
    userTimezone,
    dateFormat,
    timeFormat,
    includeSeconds
  );

  if (isLoading) {
    return <span className={className} style={style}>-</span>;
  }

  return (
    <span className={className} style={style}>
      {formattedDate}
    </span>
  );
};

/**
 * Hook version for programmatic use
 * Returns a function that formats dates using the user's timezone
 * 
 * Usage:
 * const formatDate = useUserTimezoneFormatter();
 * const formatted = formatDate(dateString);
 */
export const useUserTimezoneFormatter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tokenDecode, setTokenDecode] = useState({});
  const [dateDecode, setDateDecode] = useState({});
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Public pages (LinkPayment, InvoicePayment) have no auth token – do NOT call handleAuth (would redirect to login)
        if (!getToken()) {
          setTokenDecode({});
          setDateDecode({});
          setIsReady(true);
          return;
        }
        const res = await handleAuth(navigate, location);
        if (res?.data) {
          setTokenDecode(res.data || {});
          setDateDecode(res?.themes || {});
          setIsReady(true);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setIsReady(true); // Set ready even on error to prevent infinite loading
      }
    };

    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- use pathname only; full `location` retriggers handleAuth every render (token_data storm).
  }, [navigate, location.pathname]);

  return (date, options = {}) => {
    const {
      dateFormat: customDateFormat,
      timeFormat: customTimeFormat,
      includeSeconds = true,
      excludeTime = false,
    } = options;

    if (!isReady || !date) {
      return "-";
    }

    const userTimezone = tokenDecode?.Timezone || "America/New_York";
    const dateFormat = customDateFormat || dateDecode?.Format || "MM/DD/YYYY";
    const timeFormat = customTimeFormat || tokenDecode?.TimeFormat || "12h";

    return formatDateWithTimezone(
      date,
      userTimezone,
      dateFormat,
      timeFormat,
      includeSeconds,
      excludeTime
    );
  };
};

export default UserTimezoneDateFormatter;
